import { Megaphone } from "lucide-react";
import React from "react";
import {
  Create,
  Datagrid,
  Edit,
  List,
  SimpleForm,
  TextField,
  NumberInput,
  TextInput,
  NumberField,
  required,
  BooleanInput,
  BooleanField,
  ReferenceInput,
  AutocompleteInput,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { ColorInput } from "react-admin-color-picker";
import { CardListActions } from "../../components";
import { filterByName } from "../../components/utils";

const PageDescription = () => (
  <div className="page-description">
    <h1>Lecture Messages</h1>
    <p>
      This page displays a list of all messages that appear in a message bar
      above videos on the platform. They can be associated with specific
      speakers, or can be general.
    </p>
  </div>
);

const AddEditPageDescription = () => (
  <div className="page-description">
    <h1>Add or Edit a Lecture Message</h1>
    <p>
      Configure the message shown above lecture players on the website and app.
      Templates may use <code>$speaker</code>, <code>$determiner</code>,
      <code>$ambassadorID</code>, <code>$link_text</code>, and
      <code>$phone_number</code>.
    </p>
  </div>
);

const filters = [
  <NumberInput source="id" label="Id" key="id" alwaysOn />,
  // <TextInput source="title" label="Title" key="title" alwaysOn />,
];

function ListView(props) {
  return (
    <>
      <PageDescription />
      <List
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        perPage={50}
        sort={{ field: "created_at", order: "DESC" }}
        title="Lecture Message Bar Messages"
      >
        <Datagrid rowClick="edit">
          <TextField source="id" label="ID" />
          <BooleanField source="display_active" label="Active?" />
          <TextField source="text_content" label="Legacy Message Text" />
          <TextField source="button_text" label="Button Text" />
          <TextField source="url" label="URL" />
        </Datagrid>
      </List>
    </>
  );
}

const textContentLengthValidator = (textContent, record) =>
  record.show_button || record.show_photo
    ? textContent.length < 100
      ? undefined
      : "Text Content is max 100 characters when button or photo are shown"
    : textContent.length < 150
      ? undefined
      : "Text Content is max 150 characters";

// Match the app and website token so admins can save one cross-client template.
const knownVariables = [
  "speaker",
  "determiner",
  "ambassadorID",
  "link_text",
  "phone_number",
];
const textContentVariablesValidator = (textContent) => {
  const regex = /\$([A-Za-z_]+)/g; // Regular expression to find tokens starting with '$' followed by one or more word characters.
  let match;

  while ((match = regex.exec(textContent)) !== null) {
    const variableName = match[1]; // Extract the variable name (without the '$').

    if (!knownVariables.includes(variableName)) {
      return `Unknown variable in Text Content: ${variableName}`; // If the variable name is not in the knownVariables array, return error.
    }
  }

  return undefined; // If all variables are valid, return true.
};

const buttonTextLengthValidator = (buttonText, record) =>
  record.show_button_icon
    ? (buttonText?.length || 0) < 8
      ? undefined
      : "Button text is max 8 characters when it has an icon"
    : (buttonText?.length || 0) < 9
      ? undefined
      : "Button text is max 9 characters";
// Document cross-client behavior here because legacy fields remain required
// for compatibility even when newer clients display different fields.
const LectureMessageForm = (redirect) => {
  // const { data: existingLectureMessages, isLoading: isPrerollLoading } = useGetList(
  //   "preroll_ads",
  //   {
  //     pagination: { page: 1, perPage: 10000 }, // Correct structure
  //     sort: { field: "id", order: "ASC" },
  //   },
  // );
  // // Extract all assigned speaker IDs from preroll ads
  // const speakerIdsWithLectureMessages = new Set(
  //   existingLectureMessages?.flatMap((ad) => ad.speaker_ids || []) || [],
  // );
  return (
    <SimpleForm redirect={redirect}>
      <AddEditPageDescription />
      <TextInput
        source="title"
        label="Title Template"
        helperText="Bold, single-line headline. Safe variables: $speaker, $determiner, and $ambassadorID."
        validate={[required(), textContentVariablesValidator]}
        fullWidth
      />
      <TextInput
        source="description"
        label="Description Template"
        helperText="Main body when speaker-specific donation or campaign data is available. Use $link_text and $phone_number for inline links."
        validate={[required(), textContentVariablesValidator]}
        fullWidth
      />
      <TextInput
        source="text_content"
        label="Legacy Message Text (required)"
        helperText="Complete standalone message used by older app versions. New clients do not display it, but the app still requires a value."
        validate={[
          required(),
          // textContentLengthValidator,
          textContentVariablesValidator,
        ]}
        fullWidth
      />
      <TextInput
        source="generic_text_content"
        label="Generic Description"
        helperText="Fallback body when speaker-specific donation or campaign data is unavailable. Write it so it makes sense without speaker variables."
        validate={[required(), textContentVariablesValidator]}
        fullWidth
      />
      <BooleanInput
        source="display_active"
        label="Is Active?"
        helperText="Only active messages can appear. Keep at most one active global message because fallback selection is not ordered."
        validate={[required()]}
      />
      <BooleanInput
        source="show_button"
        label="Show Button?"
        helperText="Displays a CTA button. Clients may prefer a speaker's donation URL over the configured URL while one exists."
      />
      <TextInput
        source="button_text"
        label="Button Text"
        helperText="Visible CTA button label, such as Donate Now. Used only when Show Button is enabled."
        // validate={[buttonTextLengthValidator]}
      />
      <BooleanInput
        source="show_button_icon"
        label="Show Button Icon?"
        helperText="Adds a heart icon in the app. The website currently ignores this setting."
      />
      <BooleanInput
        source="is_phone_callable"
        label="Phone Link Makes a Call?"
        helperText="Enabled opens the phone dialer; disabled opens a text message. If enabled without a Phone Number, the app rejects the entire message."
      />
      <TextInput
        source="phone_number"
        label="Phone Number"
        helperText="Value inserted wherever $phone_number appears in a description."
      />
      <TextInput
        source="link_text"
        label="Inline Link Text"
        helperText="Visible clickable wording inserted wherever $link_text appears, such as Learn more."
      />
      <ColorInput
        source="highlight_color"
        label="Highlight Color"
        helperText="Website message background and app inline-link/phone highlight color."
      />
      <TextInput
        source="url"
        label="URL"
        helperText="Configured link destination. Clients may prefer a speaker's donation URL while one exists. The app requires either Inline Link Text or Show Button when a URL is set."
        validate={[required()]}
        fullWidth
      />
      <BooleanInput
        source="show_photo"
        label="Show Speaker Photo?"
        helperText="Shows the current speaker's photo when the client has an eligible photo and speaker context."
      />
      <BooleanInput
        source="show_progress_bar"
        label="Show Progress Bar?"
        helperText="Controls the website campaign-progress layout. The app may determine progress visibility from live campaign totals."
      />
      <ReferenceInput source="speaker_id" reference="speakers" label="Speaker">
        <AutocompleteInput
          // source="speaker_id"
          // optionValue="id"
          optionText={(choice) => choice?.name || ""}
          filterToQuery={filterByName}
          helperText="Optional website-specific message. Leave blank for the global message used by the app and as the website fallback."
        />
      </ReferenceInput>
      {/* <ReferenceArrayInput
        reference="speakers"
        source="speaker_ids"
        label="Speakers"
      >
        <AutocompleteArrayInput
          source="speaker_ids"
          optionValue="id"
          optionText={(choice) =>
            `${speakerIdsWithLectureMessages.has(choice.id) ? "✅" : ""} ${choice?.name || ""}`
          }
          filterToQuery={filterByName}
          fullWidth
          emptyText="Search Speaker"
        />
      </ReferenceArrayInput> */}
    </SimpleForm>
  );
};

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Lecture Message updated successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {LectureMessageForm(false)}
    </Edit>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Lecture Message created successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect("list", "lecture_messages");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {LectureMessageForm("list")}
    </Create>
  );
}

export const LectureMessages = {
  category: "database",
  name: "lecture_messages",
  icon: Megaphone,
  list: ListView,
  edit: EditView,
  create: CreateView,
  options: { label: "Lecture Messages" },
};
